import { Table, message } from "antd"
import dayjs from "dayjs"
import PropTypes from "prop-types"
import React, { useState } from "react"
import { useEffect } from "react"
import { FiBookOpen, FiMessageSquare } from "react-icons/fi"
import styled from "styled-components"

import { updateEvent } from "../../../../../api"
import FloatingButton from "../../../../_shared/components/FloatingButton"
import ScreenTitle from "../../../../_shared/components/Typography/ScreenTitle"
import { useStateValue } from "../../../../_shared/context/AppStateStore"
import useRestTable from "../../../../_shared/hooks/usePaginatedRestResource"
import BookingsBottomSheet from "./BookingsBottomSheet"
import EmailBottomSheet from "./EmailBottomSheet"
import EventEditor from "./EventEditor"

// ----------------------------------------------------------------------------

function AdminBookings(props) {
    // -------------------------------------
    // Props destructuring
    // -------------------------------------

    // -------------------------------------
    // Hooks (e.g. useState, useMemo ...)
    // -------------------------------------

    const [{ token }] = useStateValue()

    const [events, fetchEvents, isLoading, pagination, total] = useRestTable(
        "/events",
        token
    )

    const [selectedEvent, setSelectedEvent] = useState(null)
    const [selectedEmailEvent, setSelectedEmailEvent] = useState(null)
    const [selectedEditorEvent, setSelectedEditorEvent] = useState(null)

    // -------------------------------------
    // Effects
    // -------------------------------------

    useEffect(() => {
        fetchEvents()
        // eslint-disable-next-line
    }, [selectedEvent])

    // -------------------------------------
    // Component functions
    // -------------------------------------

    async function handleSaveEvent(values) {
        try {
            const body = { ...values }
            await updateEvent(values.id, body, token)
            message.success(
                values?.id ? "Evento aggiornato." : "Evento creato."
            )
            fetchEvents()
            setSelectedEditorEvent(null)
        } catch (e) {
            message.error(`Non siamo riusciti a creare l'evento: ${e}`)
        }
    }

    function closeSheet() {
        setSelectedEvent(null)
        setSelectedEmailEvent(null)
    }

    function openEventForm(event) {
        return () => setSelectedEvent(event.id)
    }

    function openEmailForm(row) {
        return () => setSelectedEmailEvent(row)
    }

    function handleTableChange(pagination) {
        fetchEvents({ pagination })
    }

    function getPaginationObject() {
        return {
            size: "small",
            total: total,
            current: pagination.current,
            pageSize: pagination.pageSize,
            showSizeChanger: false,
        }
    }

    function renderRowActions(row) {
        return (
            <div className="row-actions">
                <FloatingButton onClick={openEventForm(row)}>
                    <FiBookOpen className="icn" />
                </FloatingButton>
                <FloatingButton onClick={openEmailForm(row)}>
                    <FiMessageSquare className="icn" />
                </FloatingButton>
                {/* <FloatingButton onClick={openEditorForm(row)}>
                    <FiEdit2 className="icn" />
                </FloatingButton> */}
            </div>
        )
    }

    // -------------------------------------
    // Component local variables
    // -------------------------------------

    return (
        <div className={props.className}>
            <ScreenTitle text="Prenotazioni"></ScreenTitle>
            <div className="top-bar"></div>
            <div className="container">
                <Table
                    dataSource={events}
                    rowKey={(row) => row.id}
                    columns={getColumns(renderRowActions)}
                    loading={isLoading}
                    pagination={getPaginationObject()}
                    onChange={handleTableChange}
                    checkStrictly={true}
                />
            </div>
            <BookingsBottomSheet
                open={selectedEvent ? true : false}
                defaultValues={selectedEvent}
                onCancel={closeSheet}
                eventId={selectedEvent}
            />
            <EmailBottomSheet
                open={selectedEmailEvent ? true : false}
                onCancel={closeSheet}
                event={selectedEmailEvent}
            />
            <EventEditor
                visible={selectedEditorEvent ? true : false}
                onCancel={() => setSelectedEditorEvent(null)}
                eventData={selectedEditorEvent}
                onConfirm={handleSaveEvent}
            />
        </div>
    )
}

// ----------------------------------------------------------------------------
// Component PropTypes and default props
// ----------------------------------------------------------------------------

AdminBookings.propTypes = {
    className: PropTypes.string.isRequired,
}

AdminBookings.defaultProps = {}

// ----------------------------------------------------------------------------

const StyledAdminBookings = styled(AdminBookings)`
    & {
        h1 {
            text-align: left;
        }

        .top-bar {
            margin: 20px 0;

            display: flex;
            flex-direction: row;
            align-items: center;
        }

        .ant-table-cell {
            .row-actions {
                display: flex;
                justify-content: space-between;
            }
        }

        .table {
            tr {
                .row-actions {
                    display: flex;
                    justify-content: space-between;
                    transition: all 0.2s ease-in-out;
                    opacity: 0;
                }

                :hover {
                    .row-actions {
                        opacity: 1;
                    }
                }
            }
        }

        .icn {
            font-size: 20px;
            color: ${(props) => props?.theme?.colors?.textPrimary};
        }
    }
`
// ----------------------------------------------------------------------------

export default StyledAdminBookings

export function getColumns(renderRowActions) {
    return [
        {
            title: "Titolo",
            dataIndex: "title",
            key: "title",
            render: (text) => (
                <>
                    {text
                        ?.replace("<br>", " ")
                        .replace(/<p>/g, " ")
                        .replace(/<\/p>/g, " ")}
                </>
            ),
        },
        {
            title: "Data",
            key: "date",
            render: (row) => (
                <>{dayjs(row?.days[0]?.date)?.format("DD/MM/YYYY")}</>
            ),
        },
        {
            title: "Location",
            dataIndex: "locations",
            key: "locations",
            render: (locations) => (
                <div>
                    {locations?.map((l) => {
                        const { code, city, province, address } = l
                        return (
                            <div style={{ whiteSpace: "nowrap" }} key={code}>
                                {address}, {city} ({province})
                            </div>
                        )
                    })}
                </div>
            ),
        },
        {
            title: "Numero di prenotazioni",
            key: "bookings",
            render: (row) =>
                row?.slots?.filter((s) => (s.booker ? true : false))?.length,
        },
        {
            title: "Slot liberi",
            key: "freeSlots",
            render: (row) =>
                row?.slots?.filter((s) => (s.booker ? false : true))?.length,
        },
        {
            title: "",
            dataIndex: "",
            key: "range-actions",
            render: renderRowActions,
        },
    ]
}
